import {
  DEFAULT_SHARE_EXPIRY,
  type APIShareMine,
  type ShareExpiry,
} from '@see-stella/types'
import { sDayJs } from '@see-stella/utils'
import { useEffect, useRef, useState } from 'react'
import { logError } from '../../lib/logger'
import { plural } from '../share/format'
import type { Includes } from './useOwnerShare'
import './ShareSheet.css'

interface ShareSheetProps {
  includes: Includes
  /** The active link when one is live (Manage state), or null (Create state). */
  link: APIShareMine | null
  creating: boolean
  /** True while a Stop-sharing (revoke) request is in flight. */
  stopping: boolean
  error: string | null
  onCreate: (expiry: ShareExpiry) => void
  /** Manage: hand the live link to the OS share sheet. */
  onShare: () => void
  /** Manage: stop sharing (already confirmed in the sheet). */
  onStop: () => void
  onClose: () => void
}

const SECTION_LABELS: { key: keyof Includes; label: string }[] = [
  { key: 'photos', label: 'Photos' },
  { key: 'metrics', label: 'Metrics' },
  { key: 'routine', label: 'Routine' },
  { key: 'lifestyle', label: 'Lifestyle' },
]

// The plan's dropdown (60d exists in the enum but is intentionally not offered here).
const EXPIRY_OPTIONS: { value: ShareExpiry; label: string }[] = [
  { value: '24h', label: '1 day' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'never', label: 'Never' },
]

/**
 * The owner's share sheet. Two states from one component:
 * - **Create** (no live link): recap what will be shared, pick an expiry, mint.
 * - **Manage** (a link is live): show status/views/expiry, the URL with Copy and
 *   Share, and a confirmed Stop sharing. Flipping a control-bar toggle writes
 *   through to the same link (handled by the hook), so the recap here stays live.
 * Owner-facing copy ("You're sharing" / "Sharing right now") — the recipient-facing
 * copy lives in the shared profile itself. No em dashes (brand voice).
 */
export function ShareSheet({
  includes,
  link,
  creating,
  stopping,
  error,
  onCreate,
  onShare,
  onStop,
  onClose,
}: ShareSheetProps) {
  const [expiry, setExpiry] = useState<ShareExpiry>(DEFAULT_SHARE_EXPIRY)

  // Block dismissal while a mint is in flight — closing here would let the user toggle
  // the (now un-scrimmed) bar while `create`'s section snapshot is still in flight, so
  // the minted link could commit stale flags. The CTA shows "Creating…" meanwhile.
  const requestClose = () => {
    if (!creating) onClose()
  }

  // Escape closes the sheet (standard dialog affordance alongside the scrim), except
  // mid-mint (see requestClose).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !creating) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, creating])

  const shared = SECTION_LABELS.filter((s) => includes[s.key]).map(
    (s) => s.label
  )
  const notShared = SECTION_LABELS.filter((s) => !includes[s.key]).map(
    (s) => s.label
  )

  return (
    <div
      className='sheet'
      role='dialog'
      aria-modal='true'
      aria-label='Share your skin profile'
    >
      <button
        type='button'
        className='sheet__scrim'
        aria-label='Close'
        onClick={requestClose}
      />
      <div className='sheet__card'>
        <div className='sheet__grip' aria-hidden='true' />
        <h2 className='sheet__title'>Share your skin profile</h2>

        {link ? (
          <ManageBody
            link={link}
            shared={shared}
            notShared={notShared}
            stopping={stopping}
            error={error}
            onShare={onShare}
            onStop={onStop}
          />
        ) : (
          <CreateBody
            shared={shared}
            notShared={notShared}
            expiry={expiry}
            setExpiry={setExpiry}
            creating={creating}
            error={error}
            onCreate={onCreate}
          />
        )}
      </div>
    </div>
  )
}

// ===== Create state =====

function CreateBody({
  shared,
  notShared,
  expiry,
  setExpiry,
  creating,
  error,
  onCreate,
}: {
  shared: string[]
  notShared: string[]
  expiry: ShareExpiry
  setExpiry: (e: ShareExpiry) => void
  creating: boolean
  error: string | null
  onCreate: (expiry: ShareExpiry) => void
}) {
  return (
    <>
      <div className='sheet__recap'>
        <p className='sheet__recap-line'>
          <b>You're sharing:</b> {shared.join(' · ')}
        </p>
        {notShared.length > 0 && (
          <p className='sheet__recap-note'>
            {notShared.join(' · ')} not included.
          </p>
        )}
      </div>

      <label className='sheet__field'>
        <span className='sheet__label'>Link expires</span>
        <select
          className='sheet__select'
          value={expiry}
          disabled={creating}
          onChange={(e) => setExpiry(e.target.value as ShareExpiry)}
        >
          {EXPIRY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      {error && (
        <p className='sheet__error' role='alert'>
          {error}
        </p>
      )}

      <button
        type='button'
        className='sheet__cta'
        disabled={creating}
        onClick={() => onCreate(expiry)}
      >
        {creating ? 'Creating…' : 'Create & share'}
      </button>

      <p className='sheet__foot'>
        Anyone with the link can view it. You can change what is shared or stop
        sharing at any time.
      </p>
    </>
  )
}

// ===== Manage state =====

/**
 * A live link's status line: views + last-viewed, then expiry. Deterministic for
 * the empty cases ("Not opened yet" / "Never expires"); relative otherwise.
 */
function statusLine(link: APIShareMine): string {
  const opened =
    link.viewCount > 0
      ? `${plural(link.viewCount, 'view')}${
          link.lastViewedAt
            ? `, last viewed ${sDayJs(link.lastViewedAt).fromNow()}`
            : ''
        }`
      : 'Not opened yet'
  const expires = link.expiresAt
    ? `Expires ${sDayJs(link.expiresAt).fromNow()}`
    : 'Never expires'
  return `${opened} · ${expires}`
}

function ManageBody({
  link,
  shared,
  notShared,
  stopping,
  error,
  onShare,
  onStop,
}: {
  link: APIShareMine
  shared: string[]
  notShared: string[]
  stopping: boolean
  error: string | null
  onShare: () => void
  onStop: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [confirmingStop, setConfirmingStop] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mounted = useRef(true)

  // Track mount + clear the "Copied" reset timer on unmount so nothing fires setState
  // on an unmounted sheet — including a `writeText` that resolves after unmount (Manage
  // unmounts on close and when Stop swaps back to Create).
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (copyTimer.current !== null) clearTimeout(copyTimer.current)
    }
  }, [])

  // Only claim "Copied" after a CONFIRMED clipboard write. In an embedded WebView the
  // Clipboard API may be absent (non-secure context) or reject; a robust native bridge
  // lands with on-device verification (plan 5.4). Until then, a missing/failed clipboard
  // falls back to the OS share sheet rather than falsely reporting success.
  const onCopy = async () => {
    const clip = navigator.clipboard
    if (!clip?.writeText) {
      onShare()
      return
    }
    try {
      await clip.writeText(link.url)
    } catch (e) {
      logError('share: clipboard copy failed', e)
      // Only fall back to the OS share sheet if we are still mounted — a rejection that
      // arrives after the user closed the sheet must not pop a share sheet.
      if (mounted.current) onShare()
      return
    }
    if (!mounted.current) return // sheet closed while the write was pending
    setCopied(true)
    if (copyTimer.current !== null) clearTimeout(copyTimer.current)
    copyTimer.current = setTimeout(() => setCopied(false), 2000)
  }

  return (
    <>
      <div className='sheet__status'>
        <p className='sheet__status-live'>
          <span className='sheet__dot' aria-hidden='true' />
          Sharing is live
        </p>
        <p className='sheet__status-stats'>{statusLine(link)}</p>
      </div>

      <div className='sheet__recap'>
        <p className='sheet__recap-line'>
          <b>Sharing right now:</b> {shared.join(' · ')}
        </p>
        {notShared.length > 0 && (
          <p className='sheet__recap-note'>
            {notShared.join(' · ')} not included.
          </p>
        )}
      </div>

      <div className='sheet__linkrow'>
        <span className='sheet__url'>{link.url}</span>
        <button type='button' className='sheet__copy' onClick={onCopy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <button type='button' className='sheet__cta' onClick={onShare}>
        Share…
      </button>

      {error && (
        <p className='sheet__error' role='alert'>
          {error}
        </p>
      )}

      {confirmingStop ? (
        <div className='sheet__confirm' role='group' aria-label='Stop sharing'>
          <p className='sheet__confirm-q'>
            Stop sharing? The link stops working right away.
          </p>
          <div className='sheet__confirm-actions'>
            <button
              type='button'
              className='sheet__confirm-keep'
              disabled={stopping}
              onClick={() => setConfirmingStop(false)}
            >
              Keep sharing
            </button>
            <button
              type='button'
              className='sheet__confirm-stop'
              disabled={stopping}
              onClick={onStop}
            >
              {stopping ? 'Stopping…' : 'Yes, stop sharing'}
            </button>
          </div>
        </div>
      ) : (
        <button
          type='button'
          className='sheet__stop'
          onClick={() => setConfirmingStop(true)}
        >
          Stop sharing
        </button>
      )}

      <p className='sheet__foot'>
        Anyone with the link can view it. Toggle sections above to change what
        it shows, or stop sharing to switch it off.
      </p>
    </>
  )
}
